import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, ShieldCheck, ChevronRight, Search, X } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useVisitorCity, distanceKm, type VisitorCity } from "@/hooks/useVisitorCity";
import { useDealerUrl } from '@/hooks/useDealerUrl';

interface SellerLocationCardProps {
  listingId: string;
  userId?: string | null;
  listingCity?: string | null;     // raw city from listing
  fallbackLocation?: string | null; // e.g. dealer business_address
}

interface DealerInfo {
  business_name: string | null;
  verified: boolean;
  logo_url: string | null;
  risco_verified: boolean | null;
  city_from_address: string | null;
}

interface CityRow {
  city_display: string;
  city_norm: string;
  county_display: string;
  latitude: number | null;
  longitude: number | null;
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ș/g, "s").replace(/ț/g, "t").replace(/ă/g, "a").replace(/â/g, "a").replace(/î/g, "i")
    .trim();
}

export function SellerLocationCard({ listingId, userId, listingCity, fallbackLocation }: SellerLocationCardProps) {
  const dealerUrl = useDealerUrl(userId);
  const [dealer, setDealer] = useState<DealerInfo | null>(null);
  const [listingCoords, setListingCoords] = useState<{ lat: number; lng: number; cityDisplay: string } | null>(null);
  const { city: visitorCity, setCity } = useVisitorCity();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Fetch dealer profile + listing city coordinates
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Dealer
      if (userId) {
        const { data } = await supabase
          .from("dealer_profiles")
          .select("business_name, verified, logo_url, risco_verified, business_address")
          .eq("user_id", userId)
          .maybeSingle();
        if (!cancelled && data) {
          const addr = (data as any).business_address as string | null;
          const cityFromAddr = addr ? addr.split(",")[0]?.trim() || null : null;
          setDealer({
            business_name: data.business_name,
            verified: !!data.verified,
            logo_url: (data as any).logo_url ?? null,
            risco_verified: (data as any).risco_verified ?? null,
            city_from_address: cityFromAddr,
          });
        }
      }

      // City coords (prefer listingCity, fallback to dealer city)
      const candidates = [
        listingCity,
        fallbackLocation?.split(",")[0]?.trim(),
      ].filter(Boolean) as string[];

      for (const c of candidates) {
        const norm = normalize(c);
        const { data } = await supabase
          .from("geo_ro_city_county")
          .select("city_display, latitude, longitude")
          .eq("city_norm", norm)
          .not("latitude", "is", null)
          .limit(1);
        if (!cancelled && data && data.length > 0 && data[0].latitude != null) {
          setListingCoords({
            lat: Number(data[0].latitude),
            lng: Number(data[0].longitude),
            cityDisplay: data[0].city_display || c,
          });
          break;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId, listingCity, fallbackLocation]);

  const distance = useMemo(() => {
    if (!visitorCity || !listingCoords) return null;
    return Math.round(distanceKm(visitorCity, listingCoords));
  }, [visitorCity, listingCoords]);

  const displayCity = listingCoords?.cityDisplay || dealer?.city_from_address || listingCity || null;
  const isPrivate = !dealer?.business_name;
  const sellerName = dealer?.business_name || "Vânzător privat";
  const isVerified = dealer?.verified || dealer?.risco_verified;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-3">
      <div className="flex items-start gap-3">
        {dealer?.logo_url ? (
          <img
            src={dealer.logo_url}
            alt={sellerName}
            className="w-12 h-12 rounded-lg object-cover border border-border flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-primary font-bold text-lg">
              {sellerName.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base sm:text-lg font-bold text-foreground truncate">
              {sellerName}
            </h3>
            {isVerified && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-950/40 px-2 py-0.5 rounded-full">
                <ShieldCheck size={12} />
                Verificat
              </span>
            )}
            {isPrivate && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                Persoană fizică
              </span>
            )}
          </div>

          <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin size={14} className="text-primary flex-shrink-0" />
            <span className="truncate">
              {displayCity || "Locație nespecificată"}
              {distance !== null && (
                <>
                  {" • "}
                  <span className="text-foreground font-medium">la {distance} km de tine</span>
                </>
              )}
            </span>
          </div>

          {!visitorCity && (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
            >
              <MapPin size={12} />
              Setează orașul tău pentru a vedea distanța
            </button>
          )}
          {visitorCity && (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
            >
              Distanță calculată din {visitorCity.city} • schimbă
            </button>
          )}
        </div>
      </div>

      {!isPrivate && dealerUrl && (
        <Link
          to={dealerUrl}
          className="flex items-center justify-between text-sm text-primary hover:underline font-medium pt-2 border-t border-border"
        >
          <span>Mai multe despre vânzător</span>
          <ChevronRight size={16} />
        </Link>
      )}

      {pickerOpen && (
        <CityPicker
          current={visitorCity}
          onClose={() => setPickerOpen(false)}
          onSelect={(c) => { setCity(c); setPickerOpen(false); }}
          onClear={() => { setCity(null); setPickerOpen(false); }}
        />
      )}
    </div>
  );
}

// ---------- City Picker Modal ----------

export function CityPicker({
  current, onClose, onSelect, onClear,
}: {
  current: VisitorCity | null;
  onClose: () => void;
  onSelect: (c: VisitorCity) => void;
  onClear: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<CityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    let cancelled = false;
    const term = q.trim();
    setLoading(true);
    const run = async () => {
      const norm = normalize(term);
      let query = supabase
        .from("geo_ro_city_county")
        .select("city_display, city_norm, county_display, latitude, longitude")
        .not("latitude", "is", null)
        .order("population", { ascending: false, nullsFirst: false })
        .limit(norm.length >= 2 ? 1000 : 50);
      if (norm.length >= 2) {
        query = query.ilike("city_norm", `%${norm}%`);
      }
      const { data } = await query;
      if (!cancelled) {
        const seen = new Set<string>();
        const unique = ((data || []) as CityRow[]).filter((r) => {
          const key = `${r.city_norm}|${r.county_display}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setResults(unique);
        setLoading(false);
      }
    };
    const t = setTimeout(run, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden mt-20 sm:mt-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-foreground">Alege orașul tău</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg" aria-label="Închide">
            <X size={18} />
          </button>
        </div>

        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Caută oraș (ex: Cluj, București...)"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && results.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">Se caută...</div>
          )}
          {!loading && results.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">
              Niciun oraș găsit. Încearcă altă căutare.
            </div>
          )}
          {results.map((r) => (
            <button
              key={r.city_norm}
              onClick={() => onSelect({
                city: r.city_display,
                city_norm: r.city_norm,
                lat: Number(r.latitude),
                lng: Number(r.longitude),
              })}
              className={`w-full text-left px-4 py-3 hover:bg-muted border-b border-border last:border-0 ${current?.city_norm === r.city_norm ? "bg-primary/10" : ""}`}
            >
              <div className="font-medium text-foreground">{r.city_display}</div>
              <div className="text-xs text-muted-foreground">{r.county_display}</div>
            </button>
          ))}
        </div>

        {current && (
          <div className="p-3 border-t border-border">
            <button
              onClick={onClear}
              className="w-full text-sm text-muted-foreground hover:text-destructive py-2"
            >
              Șterge orașul selectat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
